import apiFactory from "@/helpers/api";
import lowerCase from "lodash/lowerCase";
import moment from "moment-timezone";
import truncate from "lodash/truncate";
import { i18n } from "@/i18n";
import ConfidenceLevel from "@/constants/ConfidenceLevel";
import GoalType from "@/constants/GoalType";

const {
	post: userAPIPost,
	get: userAPIGet,
	put: userAPIPut,
	del: userAPIDelete,
} = apiFactory();

const VALID_GOAL_TYPES = Object.keys( GoalType )
	.map( goalType => goalType.toLowerCase() )
	.join( ", " );

const VALID_GOAL_CONFIDENCE_LEVELS = Object.keys( ConfidenceLevel )
	.map( lowerCase )
	.join( ", " );

const validateGoalTypes = goalTypes => {
	goalTypes.forEach( goalType => {
		if ( !GoalType[ goalType.toUpperCase() ] ) {
			throw new Error( i18n.t( "api.validateGoalTypes_invalidGoalType", {
				goalType,
				validGoalTypes: VALID_GOAL_TYPES,
			} ) );
		}
	} );
};

const validateConfidenceLevels = confidenceLevels => {
	confidenceLevels.forEach( confidenceLevel => {
		if ( !ConfidenceLevel[ confidenceLevel.toUpperCase() ] ) {
			throw new Error( i18n.t( "api.validateConfidenceLevels_invalidConfidenceLevel", {
				confidenceLevel,
				validGoalConfidenceLevels: VALID_GOAL_CONFIDENCE_LEVELS,
			} ) );
		}
	} );
};

export const createGoal = async ( { newGoal, timezone, } ) => {
	if ( newGoal.id ) {
		throw new Error( i18n.t( "api.createGoal_createError" ) );
	}
	const adaptedGoal = {
		...newGoal,
		title: truncate( newGoal.title, { length: 250, } ),
		plannedStartDate: moment.tz( newGoal.plannedStartDate, timezone ).toISOString(),
		plannedFinishDate: moment.tz( newGoal.plannedFinishDate, timezone ).toISOString(),
		archived: !!newGoal.archived,
	};
	return await userAPIPost( "/goal", adaptedGoal ).then( goalResponse => {
		return { ...newGoal, ...goalResponse, };
	} );
};

export const updateGoal = async ( { editedGoal, timezone, } ) => {
	if ( !editedGoal.id ) {
		throw new Error( i18n.t( "api.updateGoal_updateError" ) );
	}
	const adaptedGoal = {
		...editedGoal,
		title: truncate( editedGoal.title, { length: 250, } ),
		plannedStartDate: moment.tz( editedGoal.plannedStartDate, timezone ).toISOString(),
		plannedFinishDate: moment.tz( editedGoal.plannedFinishDate, timezone ).toISOString(),
		archived: !!editedGoal.archived,
	};
	return userAPIPut( `/goal/${editedGoal.id}`, adaptedGoal ).then( goalResponse => {
		return { ...editedGoal, ...goalResponse, };
	} );
};

export const saveGoal = async ( { editedGoal, timezone, } ) => {
	if ( editedGoal.id ) {
		return updateGoal( { editedGoal, timezone, } );
	}
	return createGoal( { newGoal: editedGoal, timezone, } );
};

export const createKeyResult = async ( { goalId, newKeyResult, } ) => {
	if ( newKeyResult.id ) {
		throw new Error( i18n.t( "api.createKeyResult_createError" ) );
	}
	const ownedKeyResult = { ...newKeyResult, };
	ownedKeyResult.owners = newKeyResult.owners.map( owner => owner.personId );
	return userAPIPost( `/goal/${goalId}/keyresult`, ownedKeyResult );
};

export const createKeyResults = async ( { goalId, newKeyResults = [], } ) => {
	const promises = [];
	newKeyResults.forEach( newKeyResult => {
		promises.push( createKeyResult( { goalId, newKeyResult, } ) );
	} );
	return Promise.all( promises );
};

export const updateKeyResult = async ( { goalId, editedKeyResult, } ) => {
	if ( !editedKeyResult.id ) {
		throw new Error( i18n.t( "api.updateKeyResult_updateError" ) );
	}
	const ownedKeyResult = { ...editedKeyResult, };
	ownedKeyResult.owners = editedKeyResult.owners.map( owner => owner.personId );
	return userAPIPut( `/goal/${goalId}/keyresult/${editedKeyResult.id}`, ownedKeyResult );
};

export const updateKeyResults = async ( { goalId, editedKeyResults, } ) => {
	const promises = [];
	editedKeyResults.forEach( editedKeyResult => {
		promises.push( updateKeyResult( { goalId, editedKeyResult, } ) );
	} );
	return Promise.all( promises );
};

export const saveKeyResult = async ( { goalId, editedKeyResult, } ) => {
	if ( editedKeyResult.id ) {
		return updateKeyResult( { goalId, editedKeyResult, } );
	}
	return createKeyResult( { goalId, newKeyResult: editedKeyResult, } );
};

export const saveKeyResults = async ( { goalId, editedKeyResults = [], } ) => {
	const promises = [];
	editedKeyResults.forEach( editedKeyResult => {
		promises.push( saveKeyResult( { goalId, editedKeyResult, } ) );
	} );
	return Promise.all( promises );
};

export const fetchGoal = ( { goalId, } ) => {
	return userAPIGet( `/goal/${goalId}` );
};

/**
 * Generic function; do not export. Create specializations to export.
 */
const fetchMyGoals = ( {
	goalTypes = [],
	confidenceLevels = [],
	teamId = "",
	meetingId = "",
	sortDirection,
	limit,
	offset,
} = {} ) => {
	const optionalParams = {};

	if ( goalTypes.length ) {
		validateGoalTypes( goalTypes );
		optionalParams.type = goalTypes
			.map( lowerCase )
			.join( "," );
	}

	if ( confidenceLevels.length ) {
		validateConfidenceLevels( confidenceLevels );
		optionalParams.confidence = confidenceLevels
			.map( lowerCase )
			.join( "," );
	}

	if ( teamId ) {
		optionalParams.teamId = teamId;
	}

	if ( meetingId ) {
		optionalParams.meetingId = meetingId;
	}

	if ( offset !== undefined ) {
		optionalParams.offset = offset;
	}

	if ( limit ) {
		optionalParams.limit = limit;
	}

	optionalParams.sortDirection = sortDirection || "asc";

	return userAPIGet( "/goal", optionalParams );
};

export const myActiveGoals = async ( {
	confidenceLevels = [],
	teamId = "",
	meetingId = "",
	sortDirection,
	limit,
	offset,
} = {} ) => {
	const goalTypes = [ GoalType.ACTIVE, ];
	return fetchMyGoals( {
		goalTypes,
		confidenceLevels,
		teamId,
		meetingId,
		sortDirection,
		limit,
		offset,
	} );
};

export const myDraftGoals = async ( {
	confidenceLevels = [],
	teamId = "",
	meetingId = "",
	sortDirection,
	limit,
	offset,
} = {} ) => {
	const goalTypes = [ GoalType.DRAFT, ];
	return fetchMyGoals( {
		goalTypes,
		confidenceLevels,
		teamId,
		meetingId,
		sortDirection,
		limit,
		offset,
	} );
};

export const myArchivedGoals = async ( {
	confidenceLevels = [],
	teamId = "",
	meetingId = "",
	sortDirection,
	limit,
	offset,
} = {} ) => {
	const goalTypes = [ GoalType.ARCHIVED, ];
	return fetchMyGoals( {
		goalTypes,
		confidenceLevels,
		teamId,
		meetingId,
		sortDirection,
		limit,
		offset,
	} );
};

export const fetchKeyResults = async ( { goalId, } ) => {
	return userAPIGet( `/goal/${goalId}/keyresult` );
};

export const getGoal = async ( { goalId, } ) => {
	return userAPIGet( `/goal/${goalId}` );
};

export const deleteGoal = async ( { goalId, } ) => {
	return userAPIDelete( `/goal/${goalId}` );
};

export const deleteKeyResult = async ( { goalId, keyResultId, } ) => {
	return userAPIDelete( `/goal/${goalId}/keyresult/${keyResultId}` );
};

export const fetchMetadata = async () => {
	return userAPIGet( "/goal/meta", {} );
};

export const searchKeyResults = async options => {
	if ( options.goalIds.length === 0 ) {
		throw new Error( i18n.t( "api.queryKeyResult_goalIds_lengthError" ) );
	}

	const params = {};
	Object.keys( options ).forEach( x => {
		if ( options[ x ] !== undefined ) {
			params[ x ] = options[ x ];
		}
	} );

	return userAPIGet( "/keyresult", params );
};

export const fetchTeamGoals = async ( {
	teamId,
	goalTypes = [],
	confidenceLevels = [],
	limit = null,
	offset = null,
} = {} ) => {
	const url = "/goal";
	const params = {
		teamId,
	};
	if ( goalTypes.length ) {
		params.type = goalTypes.join( "," );
	}
	if ( confidenceLevels.length ) {
		params.confidence = confidenceLevels.join( "," );
	}
	if ( limit !== null ) {
		params.limit = limit;
	}
	if ( offset !== null ) {
		params.offset = offset;
	}
	return userAPIGet( url, params );
};



// WEBPACK FOOTER //
// ./src/api/goalAPI.js